import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

const SECTION_ID_PREFIX = 'shopify-section-';
const DEFAULT_SECTION_ID = 'section-rendering-product-card';
const PRODUCT_CARD_FETCH_CONCURRENCY = 4;
const PRODUCT_CARD_FETCH_MAX_RETRIES = 2;

/** @type {Map<string, Promise<string>>} */
const productCardSectionHtmlCache = new Map();
let productCardFetchActive = 0;
/** @type {Array<() => void>} */
const productCardFetchWaitQueue = [];

/** @type {WeakMap<Element, () => void>} */
const productCardAppBlockVisibilityCallbacks = new WeakMap();
const productCardAppBlockVisibilityObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const initialize = productCardAppBlockVisibilityCallbacks.get(entry.target);
            productCardAppBlockVisibilityObserver?.unobserve(entry.target);
            productCardAppBlockVisibilityCallbacks.delete(entry.target);
            initialize?.();
          }
        },
        { rootMargin: '300px 0px', threshold: 0.01 }
      )
    : null;

/** @type {WeakMap<Element, () => void>} */
const productCardCollectionInitCallbacks = new WeakMap();
const productCardCollectionInitObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const initialize = productCardCollectionInitCallbacks.get(entry.target);
            productCardCollectionInitObserver?.unobserve(entry.target);
            productCardCollectionInitCallbacks.delete(entry.target);
            initialize?.();
          }
        },
        { rootMargin: '280px 0px', threshold: 0.01 }
      )
    : null;

/**
 * Limits concurrent Section Rendering API fetches so Rebuy sliders do not overwhelm the browser.
 * @param {() => Promise<string>} task
 * @returns {Promise<string>}
 */
function runQueuedProductCardFetch(task) {
  return new Promise((resolve, reject) => {
    const execute = () => {
      productCardFetchActive += 1;
      task()
        .then(resolve, reject)
        .finally(() => {
          productCardFetchActive -= 1;
          const next = productCardFetchWaitQueue.shift();
          if (next) next();
        });
    };

    if (productCardFetchActive < PRODUCT_CARD_FETCH_CONCURRENCY) {
      execute();
    } else {
      productCardFetchWaitQueue.push(execute);
    }
  });
}

/**
 * @param {string} url
 * @param {AbortSignal} signal
 * @param {number} [attempt]
 * @returns {Promise<string>}
 */
async function fetchProductCardSectionHtml(url, signal, attempt = 0) {
  const response = await fetch(url, { signal, credentials: 'same-origin' });
  if (!response.ok) {
    const retryable = response.status === 429 || response.status >= 500;
    if (retryable && attempt < PRODUCT_CARD_FETCH_MAX_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
      return fetchProductCardSectionHtml(url, signal, attempt + 1);
    }
    throw new Error(`Product card fetch failed: ${response.status}`);
  }
  return response.text();
}

// Surfaces where a single-variant "Add to Bag" must post to the cart instead of
// opening the product popup, even when the slider is in "choose options in popup" mode.
const REBUY_DIRECT_ADD_SELECTOR = '[data-rebuy-direct-add-single-variant="1"]';

/**
 * Finds the theme block that owns this card's Rebuy widget. The same widget id can be
 * placed more than once (e.g. homepage and mini cart), so prefer the block nearest the
 * card instead of the first one in the document.
 *
 * @param {Element} productCard
 * @param {string} widgetId
 * @returns {HTMLElement | null}
 */
function findRebuySliderBlock(productCard, widgetId) {
  const blocks = document.querySelectorAll(
    `.rebuy-product-slider-block[data-rebuy-widget-id="${CSS.escape(widgetId)}"]`
  );
  const first = blocks[0] instanceof HTMLElement ? blocks[0] : null;
  if (blocks.length < 2) return first;

  for (let node = productCard.parentElement; node; node = node.parentElement) {
    for (const block of blocks) {
      if (block instanceof HTMLElement && node.contains(block)) return block;
    }
  }
  return first;
}

/**
 * Resolves Rebuy slider CTA settings from the theme block wrapper (authoritative),
 * then product-card / slider data attributes as fallback.
 *
 * @param {Element} productCard
 * @returns {{ optionsInPopup: boolean, showDisabledCtaWhenOos: boolean, directAddSingleVariant: boolean }}
 */
function getRebuyCtaSettings(productCard) {
  const widgetHost = productCard.closest('[data-rebuy-id]');
  const widgetId = widgetHost?.getAttribute('data-rebuy-id')?.trim();
  /** @type {HTMLElement | null} */
  let block = null;
  /** @type {string | undefined} */
  let optionsInPopup;
  /** @type {string | undefined} */
  let showDisabledCtaWhenOos;

  if (widgetId) {
    block = findRebuySliderBlock(productCard, widgetId);
    if (block) {
      optionsInPopup = block.dataset.rebuyOptionsInPopup;
      showDisabledCtaWhenOos = block.dataset.rebuyShowDisabledCtaWhenOos;
    }
  }

  const slider = productCard.closest('.rebuy-product-slider');
  const cardEl = productCard instanceof HTMLElement ? productCard : null;
  const sliderEl = slider instanceof HTMLElement ? slider : null;

  if (optionsInPopup == null && cardEl) {
    optionsInPopup = cardEl.dataset.rebuyOptionsInPopup ?? sliderEl?.dataset.rebuyOptionsInPopup;
  }
  if (showDisabledCtaWhenOos == null && cardEl) {
    showDisabledCtaWhenOos =
      cardEl.dataset.rebuyShowDisabledCtaWhenOos ?? sliderEl?.dataset.rebuyShowDisabledCtaWhenOos;
  }

  // Rebuy can render the widget outside the theme block, so also honour the marker
  // on the block wrapper (the mini cart rail keeps both in the drawer).
  const directAddSingleVariant =
    Boolean(productCard.closest(REBUY_DIRECT_ADD_SELECTOR)) || Boolean(block?.closest(REBUY_DIRECT_ADD_SELECTOR));

  return {
    optionsInPopup: optionsInPopup === '1',
    showDisabledCtaWhenOos: showDisabledCtaWhenOos !== '0',
    directAddSingleVariant,
  };
}

/**
 * @param {Element} productCard
 * @returns {boolean}
 */
function isRebuyProductCard(productCard) {
  return Boolean(productCard.closest('[data-rebuy-id], .rebuy-product-slider'));
}

/**
 * Mirrors rebuy-product-slider block CTA settings on deferred card API requests.
 *
 * @param {Element} productCard
 * @param {URL} url
 */
function appendRebuyCardSettingsParams(productCard, url) {
  if (!isRebuyProductCard(productCard)) return;

  const { optionsInPopup, showDisabledCtaWhenOos, directAddSingleVariant } = getRebuyCtaSettings(productCard);
  url.searchParams.set('rebuy_options_in_popup', optionsInPopup ? '1' : '0');
  url.searchParams.set('rebuy_show_disabled_cta', showDisabledCtaWhenOos ? '1' : '0');
  url.searchParams.set('rebuy_direct_add_single_variant', directAddSingleVariant ? '1' : '0');
}

/**
 * Converts Rebuy alternate CTA links / direct-add buttons into popup triggers when enabled.
 *
 * @param {Element} productCard
 */
function syncRebuyCtaPopupMode(productCard) {
  if (!isRebuyProductCard(productCard)) return;

  const { optionsInPopup, directAddSingleVariant } = getRebuyCtaSettings(productCard);
  if (!optionsInPopup) return;

  const footer = productCard.querySelector('.product-card__footer .product-card__alt-cta');
  if (!footer) return;

  const productUrl =
    productCard.getAttribute('data-product-url') ||
    productCard.querySelector('a[ref="productCardLink"]')?.getAttribute('href') ||
    '';
  if (!productUrl || productUrl === '#') return;

  const cleanUrl = productUrl.split('?')[0] || '';
  if (!cleanUrl) return;

  const chooseLink = footer.querySelector('a.product-card__alt-cta-choose');
  if (chooseLink instanceof HTMLAnchorElement) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = chooseLink.className;
    btn.setAttribute('data-open-add-to-bag', '');
    btn.setAttribute('data-product-url', cleanUrl);
    btn.innerHTML = chooseLink.innerHTML;
    chooseLink.replaceWith(btn);
  }

  const addBtn = footer.querySelector('button.product-card__alt-cta-add');
  if (addBtn instanceof HTMLButtonElement && !directAddSingleVariant) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'product-card__alt-cta-button product-card__alt-cta-choose';
    btn.setAttribute('data-open-add-to-bag', '');
    btn.setAttribute('data-product-url', cleanUrl);
    const label = addBtn.querySelector('.product-card__quick-add-text')?.textContent?.trim() || 'Add to Bag';
    btn.innerHTML = `<span class="product-card__quick-add-text">${label}</span>`;
    addBtn.replaceWith(btn);
  }
}

/**
 * Placeholder HTML for product card while loading from AJAX.
 * @returns {string}
 */
function getPlaceholderHTML() {
  return `
    <a class="product-card__link product-card__placeholder-link" href="#" ref="productCardLink" aria-hidden="true" tabindex="-1">
      <span class="visually-hidden">Loading product</span>
      <div class="product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style border-style gap-style product-card__placeholder">
        <div class="card-gallery card-gallery-default__media-wrapper product-card__placeholder-image" style="--gallery-aspect-ratio: 1;">
          <div class="product-media-container product-media-container--image card-gallery__placeholder">
            <span class="product-card__placeholder-shimmer" aria-hidden="true"></span>
          </div>
        </div>
        <div class="product-card__placeholder-lines">
          <span class="product-card__placeholder-shimmer product-card__placeholder-line"></span>
          <span class="product-card__placeholder-shimmer product-card__placeholder-line product-card__placeholder-line--short"></span>
        </div>
        <div class="product-card__placeholder-lines">
          <span class="product-card__placeholder-shimmer product-card__placeholder-line product-card__placeholder-price"></span>
        </div>
        <div class="product-card__placeholder-lines">
          <span class="product-card__placeholder-shimmer product-card__placeholder-line product-card__placeholder-line--button"></span>
        </div>
      </div>
    </a>
  `;
}

/**
 * A custom element that displays a product card.
 * When data-product-handle is set and content is empty, shows a placeholder and loads the card via Section Rendering API.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  /** @type {AbortController | null} */
  #loadAbortController = null;

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #visibilityFallbackTimer = null;

  /** @type {(() => void) | null} */
  #drawerOpenHandler = null;

  /** @type {boolean} */
  #ajaxLoadScheduled = false;

  /** @type {string | null} */
  #committedBaseSlideId = null;

  /** @type {boolean} */
  #suspendGalleryHoverPreview = false;

  /** @type {MutationObserver | null} */
  #linkHrefObserver = null;

  /** @type {MutationObserver | null} */
  #appBlockObserver = null;

  /** @type {number | null} */
  #appBlockIdleCallback = null;

  /** @type {boolean} */
  #appBlockEnhancementsInitialized = false;

  /** @type {number} */
  #appBlockCollapseFrame = 0;

  /** @returns {string[]} */
  static get observedAttributes() {
    return ['data-product-handle'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-product-handle' || oldValue === newValue || !newValue) return;

    this.#ajaxLoadScheduled = false;
    this.#syncPlaceholderAndRefs();
    this.updatedCallback?.();
    this.#finalizeCardConnection();
  }

  /**
   * True when this card is in placeholder state.
   * Optimized check to avoid string processing where possible.
   * @returns {boolean}
   */
  #isAjaxPlaceholder() {
    if (!this.hasAttribute('data-product-handle')) return false;
    if (this.hasAttribute('data-load-from-ajax')) return true;
    const link = this.querySelector('a[ref="productCardLink"]');
    if (!link) return true;
    const href = link.getAttribute('href');
    return !href || href === '#';
  }

  get productPageUrl() {
    return this.#getCleanProductPageUrl(this.getProductCardLink()?.href ?? '');
  }

  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );
    return checkedInput?.dataset.variantId || null;
  }

  getProductCardLink() {
    return this.querySelector('a[ref="productCardLink"]') || null;
  }

  #getCleanProductPageUrl(fallbackHref = '') {
    const swatchPicker = /** @type {HTMLElement | null} */ (this.querySelector('swatches-variant-picker-component'));
    const baseUrl =
      this.dataset.productUrl ||
      swatchPicker?.dataset.productUrl ||
      fallbackHref ||
      this.getProductCardLink()?.href ||
      '';

    if (!baseUrl) return '';

    const cleanUrl = new URL(baseUrl, window.location.origin);
    cleanUrl.searchParams.delete('variant');
    if (this.hasAttribute('data-preserve-variant-url')) {
      const selectedVariantId = this.getSelectedVariantId() || this.dataset.selectedVariantId;
      if (selectedVariantId) cleanUrl.searchParams.set('variant', selectedVariantId);
    }
    return cleanUrl.toString();
  }

  syncProductCardLinks() {
    this.#syncProductCardLinks();
  }

  #refreshProductCardLinkRef() {
    const link = this.getProductCardLink();
    if (link instanceof HTMLAnchorElement) {
      this.refs.productCardLink = link;
    }
  }

  #syncProductCardLinks() {
    const link = this.getProductCardLink();
    const fallbackHref = link instanceof HTMLAnchorElement ? link.href : '';
    const productUrl = this.#getCleanProductPageUrl(fallbackHref);
    if (!productUrl) return;

    if (link instanceof HTMLAnchorElement && link.getAttribute('href')?.trim() !== '') {
      link.href = productUrl;
      this.refs.productCardLink = link;
    }

    const { productTitleLink, cardGalleryLink } = this.refs;

    if (cardGalleryLink instanceof HTMLAnchorElement) {
      cardGalleryLink.href = productUrl;
    }
    if (productTitleLink instanceof HTMLAnchorElement) {
      productTitleLink.href = productUrl;
    }
  }

  #observeProductCardLink() {
    this.#linkHrefObserver?.disconnect();
    this.#linkHrefObserver = null;

    const link = this.getProductCardLink();
    if (!(link instanceof HTMLAnchorElement)) return;

    const restoreExpectedHref = () => {
      try {
        const expectedUrl = this.#getCleanProductPageUrl();
        if (expectedUrl && link.href !== expectedUrl) {
          link.href = expectedUrl;
        }
      } catch {
        // Ignore malformed href values.
      }
    };

    restoreExpectedHref();

    this.#linkHrefObserver = new MutationObserver(restoreExpectedHref);
    this.#linkHrefObserver.observe(link, { attributes: true, attributeFilter: ['href'] });
  }

  #navigateToURL = (event, url) => {
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  #syncPlaceholderAndRefs() {
    const productHandle = this.getAttribute('data-product-handle');
    const loadFromAjax = this.hasAttribute('data-load-from-ajax');
    const productCardLink = this.querySelector('a[ref="productCardLink"]');
    const hasContent = productCardLink?.getAttribute('href')?.replace(/^#\/?$/, '');
    const isAjaxMode = Boolean(productHandle) && (loadFromAjax || !hasContent);

    if (isAjaxMode && !this.querySelector('.product-card__placeholder')) {
      this.innerHTML = getPlaceholderHTML();
    }
    if (this.#isAjaxPlaceholder()) {
      this.requiredRefs = [];
    }
  }

  #finalizeCardConnection() {
    const productHandle = this.getAttribute('data-product-handle');
    const link = this.getProductCardLink();
    const hrefAttr = link?.getAttribute('href');
    const needsAjaxLoad =
      Boolean(productHandle) &&
      (!(link instanceof HTMLAnchorElement) || !hrefAttr || hrefAttr === '#' || hrefAttr === '');

    if (needsAjaxLoad && productHandle) {
      this.#scheduleDeferredAjaxLoad(productHandle);
      return;
    }

    if (!(link instanceof HTMLAnchorElement)) return;

    const collectionGridItem = this.closest('.main-collection__grid .product-grid__item');
    if (collectionGridItem instanceof HTMLElement && productCardCollectionInitObserver) {
      const imageLoading = collectionGridItem.dataset.imageLoading;
      if (imageLoading === 'eager') {
        this.#initContent();
        return;
      }

      productCardCollectionInitCallbacks.set(this, () => {
        if (!this.isConnected) return;
        this.#initContent();
      });
      productCardCollectionInitObserver.observe(this);
      return;
    }

    this.#initContent();
  }

  connectedCallback() {
    this.#syncPlaceholderAndRefs();
    super.connectedCallback();
    this.#finalizeCardConnection();
  }

  /**
   * OPTIMIZED: Eliminates forced reflows by avoiding `getComputedStyle` and `getBoundingClientRect` calls.
   * Performs read operations using safe DOM attributes and lightweight properties.
   */
  // change 1
  #collapseHiddenAppBlocks() {
    const content = this.querySelector('.product-card__content');
    if (!content) return;

    const blocks = content.querySelectorAll('.shopify-app-block, .shopify-block');
    const isCollectionGridCard = Boolean(this.closest('.main-collection__grid .product-grid__item'));

    for (const block of blocks) {
      const meaningfulChildren = Array.from(block.children).filter(
        (child) => !['SCRIPT', 'LINK', 'STYLE'].includes(child.tagName)
      );

      const hasMeaningfulContent =
        meaningfulChildren.length > 0 || Boolean(block.textContent?.trim());

      if (!hasMeaningfulContent) {
        // The observer watches hidden: writing the same value reschedules this scan.
        if (!isCollectionGridCard || block.getAttribute('hidden') !== '') {
          block.setAttribute('hidden', '');
        }
        if (!isCollectionGridCard || !block.classList.contains('product-card__app-block--collapsed')) {
          block.classList.add('product-card__app-block--collapsed');
        }
      }
    }
  }
  // change 1
  // change 2
  #scheduleCollapseHiddenAppBlocks() {
    if (this.#appBlockCollapseFrame) return;

    const schedule = () => {
      this.#appBlockCollapseFrame = 0;

      if (!this.isConnected) return;

      this.#collapseHiddenAppBlocks();
    };

    if (typeof requestIdleCallback === 'function') {
      this.#appBlockCollapseFrame = requestIdleCallback(schedule, {
        timeout: 2000,
      });
    } else {
      this.#appBlockCollapseFrame = window.setTimeout(schedule, 100);
    }
  }
  // change 2

  /**
   * OPTIMIZED: MutationObserver restricted strictly to `hidden` to avoid heavy subtree tracking.
   */
  // change 3
  #observeHiddenAppBlocks() {
      const content = this.querySelector('.product-card__content');
      if (!content || this.#appBlockObserver) return;

      this.#appBlockObserver = new MutationObserver(() => {
        this.#scheduleCollapseHiddenAppBlocks();
      });

      this.#appBlockObserver.observe(content, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden'],
      });
  }
  // change 3

  #scheduleAppBlockEnhancements() {
    if (
      this.#appBlockEnhancementsInitialized ||
      !this.querySelector('.shopify-app-block, .shopify-block')
    ) {
      return;
    }

    const initialize = () => {
      if (this.#appBlockEnhancementsInitialized || !this.isConnected) return;
      this.#appBlockEnhancementsInitialized = true;
      productCardAppBlockVisibilityObserver?.unobserve(this);
      productCardAppBlockVisibilityCallbacks.delete(this);

      const run = () => {
        this.#appBlockIdleCallback = null;
        if (!this.isConnected) return;
        this.#collapseHiddenAppBlocks();
        this.#observeHiddenAppBlocks();
      };

      if (typeof requestIdleCallback === 'function') {
        // this.#appBlockIdleCallback = requestIdleCallback(run, { timeout: 2000 });
        // change 5
           this.#appBlockIdleCallback = requestIdleCallback(run, { timeout: 1000 });
        // change 5
      } else {
        this.#appBlockIdleCallback = window.setTimeout(run, 100);
      }
    };

    if (!productCardAppBlockVisibilityObserver) {
      initialize();
      return;
    }

    productCardAppBlockVisibilityCallbacks.set(this, initialize);
    productCardAppBlockVisibilityObserver.observe(this);
  }

  #resetSliderSwatchesScroll() {
    if (!this.classList.contains('product-slider__card')) return;

    this.querySelectorAll('.variant-option__swatches-form').forEach((form) => {
      if (form instanceof HTMLElement) {
        form.scrollLeft = 0;
      }
    });
  }

  #applyPlpImageLoadingFromGridItem() {
    const gridItem = this.closest('.product-grid__item');
    if (!gridItem) return;

    const { imageLoading, fetchpriority } = gridItem.dataset;
    if (!imageLoading && !fetchpriority) return;

    const img = this.querySelector('.product-media__image');
    if (!(img instanceof HTMLImageElement)) return;

    if (imageLoading === 'eager' || imageLoading === 'lazy') {
      img.loading = imageLoading;
    }
    if (fetchpriority) {
      img.setAttribute('fetchpriority', fetchpriority);
    }
  }

  #initContent() {
    this.#applyPlpImageLoadingFromGridItem();
    syncRebuyCtaPopupMode(this);
    this.#refreshProductCardLinkRef();
    this.#syncProductCardLinks();

    const isCollectionCard = Boolean(this.closest('.product-grid__item'));
    if (!isCollectionCard) {
      this.#observeProductCardLink();
    }

    this.#scheduleAppBlockEnhancements();
    this.#resetSliderSwatchesScroll();

    if (!isCollectionCard) {
      requestAnimationFrame(() => {
        this.#refreshProductCardLinkRef();
        this.#syncProductCardLinks();
        this.#resetSliderSwatchesScroll();
      });
    }

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.addEventListener('click', this.navigateToProduct);

    this.#syncCommittedBaseToInitialSlide();

    const schedulePreload = () => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(schedulePreload, { timeout: 2500 });
    } else {
      setTimeout(schedulePreload, 200);
    }
  }

  #scheduleDeferredAjaxLoad(productHandle) {
    if (this.#ajaxLoadScheduled || !this.isConnected || !this.#isAjaxPlaceholder()) return;
    this.#ajaxLoadScheduled = true;

    const startLoad = () => {
      if (!this.isConnected || !this.#isAjaxPlaceholder()) return;

      const scrollRoot = this.closest('.custom-container__cards');
      if (scrollRoot instanceof HTMLElement) {
        this.#observeVisibleThenLoad(productHandle, scrollRoot);
        return;
      }

      const schedule =
        typeof requestIdleCallback === 'function'
          ? (/** @type {IdleRequestCallback} */ fn) => requestIdleCallback(fn, { timeout: 1000 })
          : (/** @type {() => void} */ fn) => setTimeout(fn, 50);

      schedule(() => {
        if (this.isConnected && this.#isAjaxPlaceholder()) {
          void this.#loadProductCardFromAjax(productHandle);
        }
      });
    };

    const drawer = this.closest('cart-drawer-component dialog');
    if (drawer instanceof HTMLDialogElement && !drawer.open) {
      this.#drawerOpenHandler = () => {
        if (this.#drawerOpenHandler) {
          document.removeEventListener('cart-drawer:open', this.#drawerOpenHandler);
          this.#drawerOpenHandler = null;
        }
        requestAnimationFrame(() => requestAnimationFrame(startLoad));
      };
      document.addEventListener('cart-drawer:open', this.#drawerOpenHandler);
      return;
    }

    startLoad();
  }

  #observeVisibleThenLoad(productHandle, scrollRoot) {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#clearVisibilityObserver();
        void this.#loadProductCardFromAjax(productHandle);
      },
      { root: scrollRoot, rootMargin: '0px 200px 0px 200px', threshold: 0.01 }
    );
    this.#visibilityObserver.observe(this);

    this.#visibilityFallbackTimer = setTimeout(() => {
      this.#visibilityFallbackTimer = null;
      if (!this.#isAjaxPlaceholder()) return;
      this.#clearVisibilityObserver();
      void this.#loadProductCardFromAjax(productHandle);
    }, 6000);
  }

  #clearVisibilityObserver() {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    if (this.#visibilityFallbackTimer) {
      clearTimeout(this.#visibilityFallbackTimer);
      this.#visibilityFallbackTimer = null;
    }
  }

  #applyProductCardSectionHtml(html, sectionId) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const normalizedId = sectionId.replace(/^shopify-section-/, '');
    const sectionEl =
      doc.getElementById(`${SECTION_ID_PREFIX}${normalizedId}`) ??
      doc.querySelector('.shopify-section') ??
      doc.body?.firstElementChild;
    const productCard = sectionEl?.querySelector('product-card');

    if (productCard?.hasAttribute('data-tjc-membership')) {
      const sliderCard = this.closest('.custom-container__card, .rebuy-product-block, .rebuy-product-item');
      if (sliderCard) {
        sliderCard.remove();
      } else {
        this.remove();
      }
      return false;
    }

    const cardContent = productCard?.innerHTML;
    if (!cardContent?.trim()) {
      throw new Error('Product card content not found in section response');
    }

    this.innerHTML = cardContent;
    syncRebuyCtaPopupMode(this);
    this.requiredRefs = ['productCardLink'];
    this.updatedCallback?.();
    this.#updateRefsForAjaxLoad();
    this.#initContent();
    return true;
  }

  async #loadProductCardFromAjax(productHandle) {
    const sectionId = this.getAttribute('data-section-id') || DEFAULT_SECTION_ID;
    const productPath = `products/${productHandle}`;
    const url = new URL(productPath, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    appendRebuyCardSettingsParams(this, url);

    this.#loadAbortController?.abort();
    this.#loadAbortController = new AbortController();
    const { signal } = this.#loadAbortController;
    const cacheKey = url.toString();

    try {
      let htmlPromise = productCardSectionHtmlCache.get(cacheKey);
      if (!htmlPromise) {
        htmlPromise = runQueuedProductCardFetch(() => fetchProductCardSectionHtml(cacheKey, signal)).catch(
          (error) => {
            productCardSectionHtmlCache.delete(cacheKey);
            throw error;
          }
        );
        productCardSectionHtmlCache.set(cacheKey, htmlPromise);
      }

      const html = await htmlPromise;
      if (signal.aborted || !this.isConnected) return;
      this.#applyProductCardSectionHtml(html, sectionId);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.warn('loadProductCardFromAjax failed:', err.message);
      if (this.isConnected) this.#showPlaceholderError();
    } finally {
      this.#loadAbortController = null;
    }
  }

  #updateRefsForAjaxLoad() {
    if (typeof this.updatedCallback === 'function') {
      this.updatedCallback();
    }
  }

  #showPlaceholderError() {
    const link = this.querySelector('.product-card__placeholder-link');
    if (link) {
      link.innerHTML = `
        <span class="visually-hidden">Failed to load product</span>
        <div class="product-card__content product-card__placeholder product-card__placeholder--error">
          <p class="product-card__placeholder-error-text">Unable to load product</p>
        </div>
      `;
    }
  }

  disconnectedCallback() {
    this.#loadAbortController?.abort();
    this.#loadAbortController = null;
    this.#clearVisibilityObserver();

    if (this.#drawerOpenHandler) {
      document.removeEventListener('cart-drawer:open', this.#drawerOpenHandler);
      this.#drawerOpenHandler = null;
    }

    this.#ajaxLoadScheduled = false;
    this.#linkHrefObserver?.disconnect();
    this.#linkHrefObserver = null;
    this.#appBlockObserver?.disconnect();
    this.#appBlockObserver = null;

    productCardAppBlockVisibilityObserver?.unobserve(this);
    productCardAppBlockVisibilityCallbacks.delete(this);

    if (this.#appBlockIdleCallback !== null) {
      if (typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(this.#appBlockIdleCallback);
      } else {
        clearTimeout(this.#appBlockIdleCallback);
      }
      this.#appBlockIdleCallback = null;
    }

    this.#appBlockEnhancementsInitialized = false;
    // change 4
    if (this.#appBlockCollapseFrame) {
      if (typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(this.#appBlockCollapseFrame);
      } else {
        clearTimeout(this.#appBlockCollapseFrame);
      }
      this.#appBlockCollapseFrame = 0;
    }
    // change 4
    cancelAnimationFrame(this.#hoverResetFrame);
    this.#hoverResetFrame = 0;

    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  #loadDeferredHoverImages() {
    const images = Array.from(this.querySelectorAll('[data-deferred-card-image]')).filter(
      (image) => image instanceof HTMLImageElement
    );
    if (!images.length) return null;

    return Promise.all(
      images.map(
        (image) =>
          new Promise((resolve) => {
            const picture = image.closest('picture');
            const markLoaded = () => {
              const loaded = image.naturalWidth > 1;
              if (loaded) image.closest('.sirv-plp-tile')?.classList.add('sirv-plp-tile--hover-loaded');
              resolve(loaded);
            };

            const sources = picture?.querySelectorAll('source[data-srcset]');
            if (sources) {
              for (let i = 0; i < sources.length; i++) {
                const source = sources[i];
                source.setAttribute('srcset', source.getAttribute('data-srcset') ?? '');
                source.removeAttribute('data-srcset');
              }
            }

            image.addEventListener('load', markLoaded, { once: true });
            image.addEventListener('error', () => resolve(false), { once: true });
            if (image.dataset.src) image.src = image.dataset.src;
            image.removeAttribute('data-src');
            image.removeAttribute('data-deferred-card-image');
            picture?.removeAttribute('data-deferred-card-picture');
            if (image.complete) markLoaded();
          })
      )
    );
  }

  #syncCommittedBaseToInitialSlide() {
    let { slideshow } = this.refs;
    if (!slideshow) {
      const slideshowEl = this.querySelector('[ref="slideshow"]');
      if (slideshowEl) {
        this.refs.slideshow = /** @type {import('slideshow').Slideshow} */ (slideshowEl);
      }
      slideshow = this.refs.slideshow;
    }

    if (!slideshow) return;

    const initialSlideId = slideshow.initialSlide?.getAttribute('slide-id');
    if (initialSlideId) {
      this.#committedBaseSlideId = initialSlideId;
    }
  }

  #setCommittedBaseSlideId(id) {
    if (typeof id === 'string' && id.length > 0) {
      this.#committedBaseSlideId = id;
      return;
    }

    this.#syncCommittedBaseToInitialSlide();
  }

  #getCommittedBaseSlideId(slideshow) {
    const visibleSlides = slideshow.slides ?? [];

    if (this.#committedBaseSlideId) {
      const matchingVisibleSlide = visibleSlides.find(
        (slide) => slide.getAttribute('slide-id') === this.#committedBaseSlideId
      );

      if (matchingVisibleSlide) {
        return this.#committedBaseSlideId;
      }
    }

    const initialSlideId = slideshow.initialSlide?.getAttribute('slide-id');
    if (initialSlideId) {
      this.#committedBaseSlideId = initialSlideId;
      return initialSlideId;
    }

    return null;
  }

  #restoreCommittedBaseSlide(slideshow) {
    const committedBaseSlideId = this.#getCommittedBaseSlideId(slideshow);
    if (!committedBaseSlideId) return false;

    slideshow.select({ id: committedBaseSlideId }, undefined, { animate: false });
    return true;
  }

  #getHoverPreviewIndex(slideshow) {
    const slides = slideshow.slides;
    if (!slides || slides.length < 2) return null;

    const committedBaseSlideId = this.#getCommittedBaseSlideId(slideshow);
    const committedBaseSlide = committedBaseSlideId
      ? slides.find((slide) => slide.getAttribute('slide-id') === committedBaseSlideId)
      : null;
    const baseIndex = committedBaseSlide ? slides.indexOf(committedBaseSlide) : 0;

    for (let index = baseIndex + 1; index < slides.length; index += 1) {
      if (!slides[index].hasAttribute('hidden')) return index;
    }

    return null;
  }

  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }

    this.#applyOptimisticCardPrice();
  };

  /**
   * Paint the selected swatch price immediately on PLP cards and CX sliders.
   * PLP shows `.price-block__default-price`; CX sliders show `.price-block__cx-selected-price`.
   */
  #applyOptimisticCardPrice() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('swatches-variant-picker-component input[type="radio"]:checked[data-price]')
    );
    const priceText = checkedInput?.dataset.price?.trim();
    if (!priceText) return;

    const compareText = checkedInput.dataset.compareAtPrice?.trim() || '';
    const priceSpan = `<span class="price">${priceText}</span>`;
    const compareSpan = compareText ? `<span class="compare-at-price">${compareText}</span>` : '';
    const priceHtml = `${priceSpan}${compareSpan}`;

    const selectedHost = this.querySelector('.price-block__cx-selected-price');
    const defaultHost = this.querySelector('.price-block__default-price');
    const isCxSlider = Boolean(this.closest('.cx-product-slider'));
    const priceHost = isCxSlider
      ? selectedHost || this.querySelector('product-price')
      : defaultHost || this.querySelector('product-price');
    if (!(priceHost instanceof HTMLElement)) return;

    if (
      priceHost.classList.contains('price-block__cx-selected-price') ||
      priceHost.classList.contains('price-block__default-price')
    ) {
      priceHost.innerHTML = priceHtml;
      if (!isCxSlider && selectedHost instanceof HTMLElement && selectedHost !== priceHost) {
        selectedHost.innerHTML = priceHtml;
      }
      return;
    }

    const existingPrice = priceHost.querySelector('.price:not(.price--from)');
    const existingFrom = priceHost.querySelector('.price--from');
    const existingCompare = priceHost.querySelector('.compare-at-price');

    if (existingFrom) existingFrom.remove();

    if (existingPrice) {
      existingPrice.textContent = priceText;
      existingPrice.classList.remove('price--from');
    } else {
      priceHost.insertAdjacentHTML('afterbegin', priceSpan);
    }

    if (compareText) {
      if (existingCompare) {
        existingCompare.textContent = compareText;
      } else {
        priceHost.insertAdjacentHTML('beforeend', compareSpan);
      }
    } else if (existingCompare) {
      existingCompare.remove();
    }
  }

  #handleVariantUpdate = (event) => {
    this.updatePrice(event);
    this.#updateProductTitle(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#setCommittedBaseSlideId(this.variantPicker?.selectedOption?.dataset.optionMediaId ?? null);
    this.#suspendGalleryHoverPreview = false;
    this.#previousSlideIndex = null;

    this.removeAttribute('data-no-swatch-selected');
    this.#updateOverflowList();

    const slideshowEl = this.querySelector('[ref="slideshow"]');
    if (slideshowEl) {
      this.refs.slideshow = /** @type {import('slideshow').Slideshow} */ (slideshowEl);
    }

    this.#refreshProductCardLinkRef();
    this.#syncProductCardLinks();
  };

  #updateOverflowList() {
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = Boolean(
      overflowList?.hasAttribute('data-plp-modal-preview') || overflowList?.querySelector('[slot="overflow"]')
    );
    if (!overflowList || !isActiveOverflowList) return;

    requestAnimationFrame(() => {
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  updatePrice(event) {
    const currentProductId = this.dataset.productId;
    const nextProductId = event.detail.data.newProduct?.id || currentProductId;
    const priceElement =
      (currentProductId && this.querySelector(`product-price[data-product-id="${currentProductId}"]`)) ||
      this.querySelector('product-price');
    const doc = event.detail.data.html;
    const newPriceElement =
      (nextProductId && doc.querySelector(`product-price[data-product-id="${nextProductId}"]`)) ||
      (currentProductId && doc.querySelector(`product-price[data-product-id="${currentProductId}"]`)) ||
      doc.querySelector('product-price');

    if (!newPriceElement || !priceElement) return;

    if (nextProductId) priceElement.setAttribute('data-product-id', String(nextProductId));
    this.#replaceCardPrice(priceElement, newPriceElement);
  }

  /**
   * Copy price HTML instead of morphing. Morph moves nodes out of the fetched
   * document, then product-price.js writes that emptied markup back and the
   * PLP price disappears. Dual wrappers: PLP shows default, CX shows selected.
   * @param {Element} priceElement
   * @param {Element} newPriceElement
   */
  #replaceCardPrice(priceElement, newPriceElement) {
    const nextSelected = newPriceElement.querySelector('.price-block__cx-selected-price');
    const nextDefault = newPriceElement.querySelector('.price-block__default-price');
    const nextPriceHtml = (
      nextSelected?.innerHTML ||
      nextDefault?.innerHTML ||
      newPriceElement.innerHTML
    ).trim();

    if (!nextPriceHtml) return;

    const isCxSlider = Boolean(this.closest('.cx-product-slider'));
    const currentSelected = priceElement.querySelector('.price-block__cx-selected-price');
    const currentDefault = priceElement.querySelector('.price-block__default-price');

    if (isCxSlider && currentSelected) {
      currentSelected.innerHTML = nextSelected?.innerHTML?.trim() || nextPriceHtml;
      return;
    }

    if (currentDefault) {
      currentDefault.innerHTML = nextPriceHtml;
      if (currentSelected) currentSelected.innerHTML = nextPriceHtml;
      return;
    }

    priceElement.innerHTML = nextPriceHtml;
  }

  #updateProductTitle(event) {
    if (!this.hasAttribute('data-preserve-variant-url')) return;

    const currentTitle = this.querySelector('product-title');
    const nextTitle = event.detail.data.html?.querySelector('product-title');
    if (!(currentTitle instanceof HTMLElement) || !(nextTitle instanceof HTMLElement)) return;

    morph(currentTitle, nextTitle);
  }

  #updateProductUrl(event) {
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    const newProduct = event.detail.data.newProduct;
    const selectedVariantId = event.detail.resource?.id;
    if (this.hasAttribute('data-preserve-variant-url') && selectedVariantId) {
      this.dataset.selectedVariantId = String(selectedVariantId);
    }
    if (this.hasAttribute('data-preserve-variant-url') && newProduct?.url) {
      this.dataset.productUrl = newProduct.url;
      if (newProduct.id) this.dataset.productId = newProduct.id;
    }

    this.#refreshProductCardLinkRef();
    this.#syncProductCardLinks();

    const quickAddButton = this.querySelector('.product-card__image-quick-add-button');
    if (quickAddButton instanceof HTMLElement) {
      const url = new URL(newProduct?.url || this.productPageUrl, window.location.origin);
      if (selectedVariantId) url.searchParams.set('variant', String(selectedVariantId));
      quickAddButton.dataset.productUrl = url.toString();
    }
  }

  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (let i = 0; i < allVariants.length; i++) {
      this.#toggleAddToCartButton(allVariants[i].dataset.optionAvailable === 'true');
    }
  }

  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  /** @type {number | null} */
  #previousSlideIndex = null;

  /** @type {number} */
  #hoverResetFrame = 0;

  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#suspendGalleryHoverPreview = true;
      this.#previousSlideIndex = event.detail.index;
    }
  };

  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  previewImage(event) {
    if (event.pointerType !== 'mouse') return;
    if (this.#suspendGalleryHoverPreview) return;

    const pendingImages = this.#loadDeferredHoverImages();
    if (pendingImages) {
      void pendingImages.then((loadedImages) => {
        if (loadedImages.every(Boolean) && this.querySelector('card-gallery')?.matches(':hover')) {
          this.#selectHoverPreview();
        }
      });
      return;
    }

    this.#selectHoverPreview();
  }

  #selectHoverPreview() {
    let { slideshow } = this.refs;
    if (!slideshow) {
      const slideshowEl = this.querySelector('[ref="slideshow"]');
      if (slideshowEl) {
        this.refs.slideshow = /** @type {import('slideshow').Slideshow} */ (slideshowEl);
      }
      slideshow = this.refs.slideshow;
    }

    if (!slideshow) return;

    this.resetVariant.cancel();

    slideshow.setAttribute('actioned', '');
    const previewIndex = this.#getHoverPreviewIndex(slideshow);
    if (previewIndex == null) return;

    slideshow.select(previewIndex, undefined, { animate: false });
    setTimeout(() => this.#preloadNextPreviewImage());
  }

  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    cancelAnimationFrame(this.#hoverResetFrame);
    this.#hoverResetFrame = requestAnimationFrame(() => {
      this.#hoverResetFrame = 0;

      const gallery = this.querySelector('card-gallery');
      if (gallery?.matches(':hover')) return;

      let { slideshow } = this.refs;
      if (!slideshow) {
        const slideshowEl = this.querySelector('[ref="slideshow"]');
        if (slideshowEl) {
          this.refs.slideshow = /** @type {import('slideshow').Slideshow} */ (slideshowEl);
        }
        slideshow = this.refs.slideshow;
      }

      if (!slideshow) return;

      this.#suspendGalleryHoverPreview = false;

      if (this.#restoreCommittedBaseSlide(slideshow)) {
        return;
      }

      this.resetVariant();
    });
  }

  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (this.#restoreCommittedBaseSlide(slideshow)) {
      return;
    }

    const cur = slideshow.current;
    if (cur > 0) {
      slideshow.select(cur - 1, undefined, { animate: false });
    }
  };

  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    if (interactiveElement) {
      return;
    }

    const link = this.getProductCardLink();
    if (!link?.href) return;
    const linkURL = new URL(this.#getCleanProductPageUrl(link.href));

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    if (targetLink instanceof HTMLAnchorElement && targetLink === link) {
      event.preventDefault();
      this.#navigateToURL(event, linkURL);
      return;
    }

    if (targetLink instanceof HTMLAnchorElement && targetLink !== link) {
      return;
    }

    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverSelectTimer;

  connectedCallback() {
    super.connectedCallback();

    this.parentProductCard = this.closest('product-card');
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));

    if (this.hasAttribute('data-select-on-hover')) {
      this.addEventListener('pointerover', this.#handleSwatchPointerOver);
      this.addEventListener('pointerout', this.#handleSwatchPointerOut);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#hoverSelectTimer);
    this.removeEventListener('pointerover', this.#handleSwatchPointerOver);
    this.removeEventListener('pointerout', this.#handleSwatchPointerOut);
  }

  /**
   * Hover mode selects the swatch exactly as a click would, after a short rest so
   * sweeping across the row does not fetch every variant. The delay outlasts the
   * hover prefetch in VariantPicker, so the selection reuses that request.
   * @param {PointerEvent} event
   */
  #handleSwatchPointerOver = (event) => {
    if (event.pointerType !== 'mouse') return;

    const label = event.target instanceof Element ? event.target.closest('.variant-option__button-label') : null;
    const input = label?.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement) || input.checked || input.disabled) return;

    clearTimeout(this.#hoverSelectTimer);
    this.#hoverSelectTimer = setTimeout(() => {
      if (!input.isConnected || input.checked || !label?.matches(':hover')) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }, 150);
  };

  /** @param {PointerEvent} event */
  #handleSwatchPointerOut = (event) => {
    const label = event.target instanceof Element ? event.target.closest('.variant-option__button-label') : null;
    if (!label || (event.relatedTarget instanceof Node && label.contains(event.relatedTarget))) return;
    clearTimeout(this.#hoverSelectTimer);
  };

  #handleCardVariantUrlUpdate() {
    if (this.parentProductCard instanceof ProductCard) {
      this.parentProductCard.syncProductCardLinks();
    }
  }

  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
